import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { 
  Image, 
  Loader2, 
  Download, 
  Sparkles,
  Palette,
  FileImage,
  Share2,
  Youtube,
  ShoppingBag,
  ArrowLeft,
  Home
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STYLES = [
  { id: "marketing", name: "Marketing", icon: <Sparkles className="w-4 h-4" />, desc: "Professional marketing images" },
  { id: "logo", name: "Logo", icon: <Palette className="w-4 h-4" />, desc: "Minimalist logo designs" },
  { id: "banner", name: "Banner", icon: <FileImage className="w-4 h-4" />, desc: "Wide promotional banners" },
  { id: "social", name: "Social", icon: <Share2 className="w-4 h-4" />, desc: "Social media posts" },
  { id: "thumbnail", name: "Thumbnail", icon: <Youtube className="w-4 h-4" />, desc: "Video thumbnails" },
  { id: "product", name: "Product", icon: <ShoppingBag className="w-4 h-4" />, desc: "Product showcase" }
];

export default function AIImageGeneratorPage() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("marketing");
  const [loading, setLoading] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/image-gen/generate`, {
        prompt: prompt.trim(),
        style,
        count: 1
      });

      if (response.data.success) {
        setGeneratedImages(response.data.images);
        toast.success("Image generated successfully!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate image");
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = (dataUrl, index) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `markeemix-image-${index + 1}.png`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-pink-600 to-purple-600 text-white py-12 pt-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-white/20">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-white/20 rounded-xl">
              <Image className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">AI Image Generator</h1>
              <p className="text-pink-200">Create stunning marketing images with AI</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Create Your Image</CardTitle>
                <CardDescription>Describe what you want to create</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="e.g., A professional banner for digital marketing agency with purple gradient background, modern typography"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={4}
                  className="resize-none"
                />

                <div>
                  <label className="text-sm font-medium mb-2 block">Style</label>
                  <div className="grid grid-cols-3 gap-2">
                    {STYLES.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setStyle(s.id)}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          style === s.id 
                            ? "border-purple-500 bg-purple-50 text-purple-700" 
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          {s.icon}
                          <span className="font-medium text-sm">{s.name}</span>
                        </div>
                        <p className="text-xs text-gray-500">{s.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={handleGenerate}
                  disabled={loading || !prompt.trim()}
                  className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating... (30-60 sec)
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Image
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
              <CardContent className="pt-6">
                <h4 className="font-semibold text-purple-900 mb-3">Pro Tips:</h4>
                <ul className="space-y-2 text-sm text-purple-800">
                  <li>• Be specific about colors, style, and mood</li>
                  <li>• Mention "professional" or "high quality" for better results</li>
                  <li>• Include composition details (centered, wide, etc.)</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Output Section */}
          <div>
            {generatedImages.length > 0 ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Generated Image</CardTitle>
                    <Badge className="bg-green-100 text-green-700">Ready</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {generatedImages.map((img, index) => (
                    <div key={img.id} className="space-y-3">
                      <div className="rounded-lg overflow-hidden border">
                        <img 
                          src={img.data_url} 
                          alt="Generated" 
                          className="w-full h-auto"
                        />
                      </div>
                      <Button 
                        onClick={() => downloadImage(img.data_url, index)}
                        className="w-full"
                        variant="outline"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Image
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <Card className="h-full min-h-[400px] flex items-center justify-center bg-gray-50/50">
                <div className="text-center p-8">
                  <Image className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">Your generated image will appear here</p>
                  <p className="text-sm text-gray-400 mt-2">Enter a prompt and click Generate</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
